import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { PregnancyWellBeingStatus } from "@madmedical/medical";
import { useChart } from "../providers/chart";
import { root } from "./useStyles";
import { $timeData, datesToTimeGridIndices, normalizeGraphProps, } from "./utils";
import Evaluations from "./Evaluations";
import TimeStamps from "./TimeStamps";
import GraphSlot from "./GraphSlot";
import Crosshair from "./Crosshair";
import DragSelect from "./DragSelect";
export default function Graph({ graphProps = {}, }) {
    var _a, _b;
    const { selectedRange, onSelectRange } = useChart();
    const graphData = useMemo(() => normalizeGraphProps(graphProps), [graphProps]);
    const { slot = { x: 0, y: 0 }, predictions } = graphData !== null && graphData !== void 0 ? graphData : {};
    const timeData = useMemo(() => (graphData ? $timeData({ graphData }) : null), [graphData]);
    const initTimeGridIndices = (timeData === null || timeData === void 0 ? void 0 : timeData.timeCoords.length)
        ? datesToTimeGridIndices({
            dates: selectedRange !== null && selectedRange !== void 0 ? selectedRange : null,
            timeData,
        })
        : { from: null, to: null };
    const [commentState, setCommentState] = useState({
        comments: [],
        groupAvatarUrl: "",
        groupMonogram: "",
        hasDoctor: false,
        id: "",
        onShow: false,
        x: 0,
    });
    const [tooltip, setTooltip] = useState({
        bottom: 0,
        note: "",
        id: "",
        left: 0,
        msec: 0,
        onShow: false,
        isGrouped: false,
    });
    const [moodGroup, setMoodGroup] = useState({
        status: [
            {
                id: "",
                createdAt: "",
                status: PregnancyWellBeingStatus.Moody,
                symptoms: [],
                comment: "",
            },
        ],
        id: "",
        onShow: false,
        x: 0,
    });
    const [evaluationBgHighlight, setEvaluationBgHighlight] = useState({
        start: onSelectRange ? (_a = initTimeGridIndices === null || initTimeGridIndices === void 0 ? void 0 : initTimeGridIndices.from) !== null && _a !== void 0 ? _a : null : null,
        end: onSelectRange ? (_b = initTimeGridIndices === null || initTimeGridIndices === void 0 ? void 0 : initTimeGridIndices.to) !== null && _b !== void 0 ? _b : null : null,
        selected: -1,
    });
    // Hide tooltips on date-range change
    useEffect(() => {
        setTooltip((state) => (Object.assign(Object.assign({}, state), { onShow: false })));
        setCommentState((state) => (Object.assign(Object.assign({}, state), { onShow: false })));
        setMoodGroup((state) => (Object.assign(Object.assign({}, state), { onShow: false })));
    }, [graphData === null || graphData === void 0 ? void 0 : graphData.coords]);
    useEffect(() => {
        if (!onSelectRange)
            return;
        const $start = evaluationBgHighlight.start;
        const $end = evaluationBgHighlight.end;
        if ($start === null) {
            onSelectRange(null);
            return;
        }
        const callbackIndexEnd = $end === $start ? $end + 1 : $end;
        const timeCoordIndexStart = callbackIndexEnd && $start < callbackIndexEnd
            ? $start
            : callbackIndexEnd;
        const timeCoordIndexEnd = callbackIndexEnd && callbackIndexEnd > $start
            ? callbackIndexEnd
            : $start;
        onSelectRange(timeData && timeCoordIndexStart !== null
            ? {
                from: new Date(timeData.timeCoords[timeCoordIndexStart].msec),
                to: new Date(timeData.timeCoords[timeCoordIndexEnd].msec - 1 // 23:59:59.9999
                ),
            }
            : null);
    }, [onSelectRange, evaluationBgHighlight, timeData]);
    // #####################
    const [offsetX, setOffsetX] = useState(0);
    const anchor = useCallback((div) => {
        if (!div)
            return;
        setOffsetX(div.getBoundingClientRect().x);
    }, []);
    return graphData && timeData ? (_jsxs("div", Object.assign({ style: root, ref: anchor }, { children: [_jsx(TimeStamps, { timeData: timeData, slot: slot }), (predictions === null || predictions === void 0 ? void 0 : predictions.isPrediction) ? (_jsx(_Fragment, {})) : onSelectRange ? (_jsx(DragSelect, { bgHighlight: evaluationBgHighlight, setBgHighlight: setEvaluationBgHighlight, graphData: graphData, timeData: timeData, offsetX: offsetX })) : (_jsx(Evaluations, { graphData: graphData, timeData: timeData, evaluationBgHighlight: evaluationBgHighlight, setEvaluationBgHighlight: setEvaluationBgHighlight, setCommentState: setCommentState, setTooltip: setTooltip })), _jsx(GraphSlot, { graphData: graphData, timeData: timeData, evaluationBgHighlight: evaluationBgHighlight, setEvaluationBgHighlight: setEvaluationBgHighlight, commentState: commentState, setCommentState: setCommentState, tooltip: tooltip, setTooltip: setTooltip, moodGroup: moodGroup, setMoodGroup: setMoodGroup }), onSelectRange ? (_jsx(_Fragment, {})) : (_jsx(Crosshair, { graphData: graphData, xMsecRatio: timeData.xMsecRatio, offsetX: offsetX }))] }))) : (_jsx(_Fragment, {}));
}
